import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Ship, Heart, MapPin, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { programs, impactStats } from '../data/mock';

const OurWork = () => {
  const processSteps = [
    {
      step: 1,
      icon: Package,
      title: "Collection & Assessment",
      description: "Equipment donated through our UK network of collection hubs, healthcare facilities, and partner organizations. Each item is assessed for quality and safety.",
      image: "https://images.unsplash.com/photo-1619618691037-751d1e6c9ad1"
    },
    {
      step: 2,
      icon: Users,
      title: "Refurbishment & Preparation",
      description: "Volunteers clean, repair, and test equipment. Items are categorized, documented, and prepared for shipping with full inventory tracking.",
      image: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca"
    },
    {
      step: 3,
      icon: Ship,
      title: "Container Shipping",
      description: "Equipment is loaded into 40-foot containers and shipped to Nigeria. We handle all customs, clearance, and logistics with full transparency.",
      image: "https://images.unsplash.com/photo-1605745341112-85968b19335b"
    },
    {
      step: 4,
      icon: Heart,
      title: "Dignified Distribution",
      description: "Partner organizations assess needs and distribute equipment person-by-person. Recipients receive training and follow-up support.",
      image: "https://images.unsplash.com/photo-1565615833231-e8c91a38a012"
    }
  ];

  const impactAreas = [
    {
      title: "Urban Communities",
      description: "Working with hospitals, churches, and community centers in Lagos, Abuja, and Port Harcourt to reach individuals in dense urban settings.",
      icon: MapPin,
      color: "from-[#2D5F3F] to-[#3A7D4A]"
    },
    {
      title: "Rural Villages",
      description: "Partnering with mobile health clinics and faith-based organizations to deliver equipment to remote communities with limited access.",
      icon: MapPin,
      color: "from-[#3A7D4A] to-[#2D5F3F]"
    },
    {
      title: "Healthcare Facilities",
      description: "Equipping under-resourced hospitals, rehabilitation centers, and clinics with mobility aids to support patient care and recovery.",
      icon: Heart,
      color: "from-[#D4AF37] to-[#C19B2C]"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Work in Action</h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            From UK collection points to Nigerian communities—transparency at every step.
          </p>
        </div>
      </section>

      {/* Impact Stats Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <Card key={index} className="text-center border-2 border-gray-100 shadow-md">
                <CardContent className="p-8">
                  <div className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-2">{stat.number}</div>
                  <div className="text-sm md:text-base text-gray-600">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">The Journey: From Donation to Delivery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every wheelchair, walking frame, and mobility aid follows a carefully managed journey.
            </p>
          </div>

          <div className="space-y-16">
            {processSteps.map((step, index) => (
              <div key={step.step} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                <div className="lg:w-1/2">
                  <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-6 left-6 bg-[#D4AF37] text-white w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold shadow-lg">
                      {step.step}
                    </div>
                  </div>
                </div>
                
                <div className="lg:w-1/2">
                  <div className="bg-white rounded-2xl p-8 shadow-lg border-l-8 border-[#2D5F3F]">
                    <div className="flex items-center mb-4">
                      <div className="bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] p-3 rounded-xl mr-4">
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-[#2D5F3F]">{step.title}</h3>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Where We Work</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Reaching communities across Nigeria with strategic partnerships and local expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {impactAreas.map((area, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                <div className={`h-3 bg-gradient-to-r ${area.color}`}></div>
                <CardContent className="p-8">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${area.color} rounded-2xl mb-6`}>
                    <area.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#2D5F3F] mb-4">{area.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Equipment We Collect & Deliver</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Manual Wheelchairs", image: "https://images.unsplash.com/photo-1619618691037-751d1e6c9ad1" },
              { name: "Electric Wheelchairs", image: "https://images.unsplash.com/photo-1595687973201-0095ff7a302e" },
              { name: "Mobility Scooters", image: "https://images.unsplash.com/photo-1706700373818-263b3a2e6b52" },
              { name: "Walking Frames & Rollators", image: "https://images.unsplash.com/photo-1565615833231-e8c91a38a012" },
              { name: "Crutches & Walking Sticks", image: "https://images.unsplash.com/photo-1619618691037-751d1e6c9ad1" },
              { name: "Adjustable Beds & Hoists", image: "https://images.unsplash.com/photo-1565615833231-e8c91a38a012" }
            ].map((equipment, index) => (
              <Card key={index} className="overflow-hidden border-2 border-gray-100 hover:border-[#2D5F3F] transition-all duration-300 group">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={equipment.image}
                    alt={equipment.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-white font-bold text-lg">{equipment.name}</h3>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Want to See More Impact?</h2>
          <p className="text-xl mb-8 text-gray-200">
            Explore how you can be part of this life-changing work through donations, partnerships, or volunteering.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#C19B2C] text-white font-semibold px-10 py-6 text-lg shadow-xl transition-all duration-300 hover:scale-105">
                Get Involved
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/transparency">
              <Button size="lg" variant="outline" className="bg-white text-[#2D5F3F] hover:bg-gray-100 border-2 border-white font-semibold px-10 py-6 text-lg transition-all duration-300">
                View Our Accountability
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurWork;
